import { Attributes, Misc, NeoModel, Rules, Validation, ValueObject } from '@singularsystems/neo-core';
import { AppService, DomainTypes } from '../../DomainTypes';
import { isMandatory, MandatoryAndVisibleSetting } from '../Config/FieldConfig/FieldSetting.enum';
import InvestorInvestmentFieldConfig from '../Config/FieldConfig/InvestorInvestmentFieldConfig';
import { Display } from '@singularsystems/neo-core/dist/Model/Decorators';
import { LiquidityLadder } from '../IssuerInvestments/LiquidityLadder';

@NeoModel
export default class InvestorInvestmentDetail extends ValueObject {

    public branchId: number | null = null;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public termsAcceptedDate: Date | null = null;

    @Rules.StringLength(20)
    public areaCode: string = "";

    @Rules.StringLength(50)
    public distinctiveNo: string = "";

    @Rules.StringLength(50)
    public certificateNo: string = "";

    @Rules.StringLength(20)
    public ledgerCode: string = "";

    @Rules.StringLength(20)
    public businessUnitNo: string = "";

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public contributionDate: Date | null = null;

    public isCeded: boolean = false;

    @Rules.StringLength(250)
    public cededReason: string = "";

    public rollingNotice: boolean = false;

    public qualifyingPurpose: boolean = false;
    
    @Attributes.Nullable()
    public commissionTypeId: number | null = null;

    @Attributes.Nullable()
    public noticePeriodId: number | null = null;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public subscriptionDate: Date | null = null;

    @Attributes.Nullable()
    public productTypeId: number | null = null;

    @Display("SAP GL Code")
    @Rules.StringLength(20)
    public sapGlCode: string = "";

    public liquidityLadder: LiquidityLadder = LiquidityLadder.Include;

    @Attributes.Nullable()
    public shareTypeId: number | null = null;

    @Display("NPV?")
    public isNpv: boolean = false;

    @Attributes.Float()
    public originalBalance: number = 0.0;

    // Client only properties / methods

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);

        const isDigit = (testString: string): boolean => !/[^0-9]/.test(testString);
        rules.failWhen(c => !isDigit(c.businessUnitNo), "Field can only contain numbers");

        // Rules affected by configurable mandatory settings
        rules.failWhen(c => !c.branchId && this.isRequired("branchId"), "Branch is required.");
        rules.failWhen(c => !c.commissionTypeId && this.isRequired("commissionTypeId"), "Commission type is required.");
        rules.failWhen(c => !c.termsAcceptedDate && this.isRequired("termsAcceptedDate"), "Terms accepted date is required.");
        rules.failWhen(c => !c.contributionDate && this.isRequired("contributionDate"), "Contribution date is required.");
        rules.failWhen(c => !c.noticePeriodId && this.isRequired("noticePeriodId"), "Notice period is required.");
        rules.failWhen(c => !c.ledgerCode && this.isRequired("ledgerCode"), "Ledger code is required.");
        rules.failWhen(c => !c.areaCode && this.isRequired("areaCode"), "Area code is required.");
        rules.failWhen(c => !c.certificateNo && this.isRequired("certificateNo"), "Certificate no. is required.");
        rules.failWhen(c => !c.distinctiveNo && this.isRequired("distinctiveNo"), "Distinctive no. is required.");
        rules.failWhen(c => !c.businessUnitNo && this.isRequired("businessUnitNo"), "Field is required.");
        rules.failWhen(c => !c.productTypeId && this.isRequired("productTypeId"), "Product type is required.");
        rules.failWhen(c => !c.shareTypeId && this.isRequired("shareTypeId"), "Share type is required.");
    }

    public toString(): string {
        return "Investor Investment Detail";
    }

    private isRequired(fieldName: keyof InvestorInvestmentFieldConfig) {
        return isMandatory(AppService.get(DomainTypes.Services.DataCache).fieldConfig.investorInvestmentFields[fieldName] as MandatoryAndVisibleSetting);
    }
}